<template>
  <div class="reminder-config">
    <h2>Reminder Schedule</h2>
    <p class="hint">
      Admin only. When outbound SMS and email go out, relative to each group's
      session period. Timing is set <strong>per cadence</strong>, not per group:
      a group switching from a 1-week to a 2-week period moves onto the matching
      plan automatically.
    </p>

    <p v-if="loading" class="state">Loading schedule…</p>
    <p v-else-if="error" class="lint-error">⚠ {{ error }}</p>

    <template v-else>
      <div class="row plan-row">
        <div class="field">
          <label>Cadence plan</label>
          <div class="radios">
            <label v-for="p in plans" :key="p.interval_weeks" class="radio">
              <input
                type="radio"
                :value="p.interval_weeks"
                v-model.number="activeInterval"
              />
              {{ p.interval_weeks === 1 ? "1-week" : `${p.interval_weeks}-week` }}
              period
            </label>
          </div>
        </div>
      </div>

      <!-- Which groups this plan governs, and the per-group on/off gate.
           Its own bordered section with its own Save: everything here writes to
           a different endpoint than the schedule table below, and mixing the two
           into one Save button would make an admin editing send times
           accidentally responsible for who is silenced. -->
      <div class="gate-panel">
        <div class="gate-header">
          <span>Group reminder gate</span>
          <span class="gate-count">
            <template v-if="!groupsLoaded">checking…</template>
            <template v-else-if="affectedGroups.length">
              {{ enabledGroupCount }} of {{ affectedGroups.length }} receiving
            </template>
            <template v-else>no groups</template>
          </span>
        </div>
        <div class="gate-body">
          <p class="gate-hint">
            Groups on the {{ activeInterval }}-week cadence. Silencing one stops
            all outbound SMS and email for its participants; the dispatcher skips
            it before any message is queued. Sessions and the cadence itself are
            unaffected. Each group's timezone is shown, because send times below
            are read in it rather than in one server-wide clock.
            <template v-if="incomingGroups.length">
              Greyed groups have not reached this cadence yet; silencing one now
              takes effect when it does.
            </template>
          </p>

          <div v-if="affectedGroups.length" class="applies-chips">
            <label
              v-for="g in affectedGroups"
              :key="g.group_id"
              class="group-chip"
              :class="{
                off: !gateDraft[g.group_id],
                changed: gateDraft[g.group_id] !== gateBaseline[g.group_id],
                incoming: !!incomingFrom[g.group_id],
              }"
              :title="incomingFrom[g.group_id]
                ? `Moves onto this cadence on ${incomingFrom[g.group_id]}`
                : 'Currently on this cadence'"
            >
              <input
                type="checkbox"
                v-model="gateDraft[g.group_id]"
                :disabled="gateSaving"
              />
              {{ g.group_name }}
              <span class="chip-tz">{{ groupZone(g.group_name) }}</span>
              <span v-if="incomingFrom[g.group_id]" class="chip-from">
                from {{ incomingFrom[g.group_id].slice(5) }}
              </span>
            </label>
          </div>
          <p v-else-if="groupsLoaded && !groupsError" class="applies-note">
            No group is on this cadence, and none is scheduled to move onto it.
            These settings stay inactive until one is.
          </p>
          <p v-if="groupsError" class="lint-error">⚠ {{ groupsError }}</p>
          <p v-if="gateError" class="lint-error">⚠ {{ gateError }}</p>

          <div v-if="affectedGroups.length" class="gate-actions">
            <span class="gate-pending">
              <template v-if="gateChanges.length">
                {{ gateChanges.length }}
                {{ gateChanges.length === 1 ? "change" : "changes" }} pending
              </template>
              <template v-else>No pending changes</template>
            </span>
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="!gateChanges.length || gateSaving"
              @click="revertGate"
            >
              Discard
            </button>
            <button
              class="btn btn-sm btn-primary"
              :disabled="!gateChanges.length || gateSaving"
              @click="saveGate"
            >
              {{ gateSaving ? "Saving…" : "Save gate" }}
            </button>
          </div>
        </div>
      </div>

      <div class="table-wrap">
        <table class="sched-table">
          <!-- Widths come from <col> so the message column keeps the space its
               explanatory text needs; the three controls are sized to content. -->
          <colgroup>
            <col />
            <col class="col-channel" />
            <col class="col-when" />
            <col class="col-time" />
            <col class="col-on" />
          </colgroup>
          <thead>
            <tr>
              <th>Message</th>
              <th>Channel</th>
              <th>Check runs</th>
              <th>Time <span class="th-note">(group local)</span></th>
              <th class="center">On</th>
            </tr>
          </thead>
          <tbody>
            <!-- One block per message, one row per channel. The message
                 description and the day offset span both channels: a nudge
                 should not land on different days depending on how it is
                 delivered, so the offset is edited once and written to both. -->
            <template v-for="g in groupedRows" :key="g.message_title">
              <tr
                v-for="(r, i) in g.rows"
                :key="`${g.message_title}-${r.channel}`"
                :class="{ 'group-start': i === 0 }"
              >
                <td v-if="i === 0" :rowspan="g.rows.length">
                  <div class="m-label">{{ meta(g.message_title).label }}</div>
                  <div class="m-hint">{{ meta(g.message_title).hint }}</div>
                  <div class="m-logic">
                    <span class="m-logic-tag">Fires when</span>
                    {{ meta(g.message_title).logic }}
                    <code class="m-source">{{ meta(g.message_title).source }}</code>
                  </div>
                  <div v-if="g.contention" class="m-warn">
                    ⚠ Both channels write this message with the group's LLM.
                    Sharing one slot doubles concurrent generations, which is
                    what makes local models time out. Consider separating them.
                  </div>
                </td>

                <td class="channel-cell">{{ channelLabel(r.channel) }}</td>

                <td v-if="i === 0" :rowspan="g.rows.length">
                  <template v-if="r.day_offset !== null">
                    <div class="when-line">
                      Day
                      <input
                        type="number"
                        class="day-input"
                        min="0"
                        :max="maxOffset"
                        :value="r.day_offset"
                        @change="setOffset(g, $event)"
                      />
                      of period
                    </div>
                    <div class="m-hint">{{ offsetHint(r.day_offset) }}</div>
                  </template>
                  <template v-else>
                    <div class="when-line">Every day</div>
                    <div class="m-hint">Sent only when the rule at left is met.</div>
                  </template>
                </td>

                <td>
                  <select v-model="r.send_time">
                    <option v-for="t in TIME_SLOTS" :key="t" :value="t">{{ t }}</option>
                  </select>
                </td>
                <td class="center">
                  <input type="checkbox" v-model="r.enabled" />
                </td>
              </tr>
            </template>

            <!-- Read-only: not part of a cadence plan, shown so the page is a
                 complete picture of outbound messaging rather than a partial one. -->
            <tr v-for="f in fixedRows" :key="f.message_title" class="fixed-row group-start">
              <td>
                <div class="m-label">
                  {{ meta(f.message_title).label }}
                  <span class="ro-badge">Not cadence-based</span>
                </div>
                <div class="m-hint">{{ meta(f.message_title).hint }}</div>
                <div class="m-logic">
                  <span class="m-logic-tag">Fires when</span>
                  {{ meta(f.message_title).logic }}
                  <code class="m-source">{{ meta(f.message_title).source }}</code>
                </div>
              </td>
              <td class="channel-cell">{{ channelLabel(f.channel) }}</td>
              <td>
                <div class="when-line">{{ f.schedule_label }}</div>
                <div class="m-hint">Set in <code>{{ f.source }}</code>.</div>
              </td>
              <td class="ro-value">{{ f.send_time }}</td>
              <td class="center ro-value">{{ f.enabled ? "On" : "Off" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="sub-hint">
        Times are America/Chicago, and are offered on the hour and half hour
        because the dispatcher wakes every {{ tickMinutes }} minutes. SMS and
        email are scheduled independently, so either can lead.
      </p>

      <p v-if="planIsInert" class="mock-note">
        ⚠ <strong>This plan is stored but cannot fire yet.</strong> Which of two
        Mondays opens a {{ activeInterval }}-week period is undecidable without a
        stored cadence anchor, so the dispatcher skips these until the cadence
        table exists. Edits are saved and will take effect then.
      </p>

      <div class="button-row">
        <span class="dirty-note" v-if="dirty">Unsaved changes</span>
        <button type="button" class="btn secondary" :disabled="!dirty || saving" @click="revert">
          Revert
        </button>
        <button type="button" class="btn primary" :disabled="!dirty || saving" @click="save">
          {{ saving ? "Saving…" : "Save changes" }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
// Admin page for outbound message timing. Paired with SessionCadencePanel:
// cadence decides how long a period is, this decides when within that period
// participants hear from the system.
//
// Two structural choices worth knowing before editing this file.
//
// Keyed by interval, not by group. Reminder timing is a property of the
// cadence shape -- every 2-week group wants its nudge at the same point in the
// period -- so a group inherits its plan through its cadence rather than
// carrying its own copy.
//
// Mutable, unlike cadence. Editing a send time cannot corrupt history the way
// editing a cadence anchor can, so this is an ordinary edit-and-save form with
// no append-only machinery.
import reminderApi, {
  messageMeta,
  MESSAGE_TYPES,
  CHANNELS,
} from "../services/reminder_api";
import cadenceApi from "../services/cadence_api";
import swal from "../services/swal";

// Offered send times. Half-hour granularity is what lets the dispatcher tick
// every 30 minutes instead of every few, which keeps it cheap.
const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    out.push(`${hh}:00`, `${hh}:30`);
  }
  return out;
})();

export default {
  name: "ReminderSchedulePanel",
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      TIME_SLOTS,
      loading: false,
      loaded: false,
      saving: false,
      error: "",
      plans: [],
      // Read-only rows that are not part of any cadence plan.
      fixedRows: [],
      activeInterval: 1,
      tickMinutes: 30,
      inertIntervals: [],
      // Serialized snapshot of the last saved state, for dirty-checking and
      // Revert. Comparing against this rather than tracking edits keeps the
      // dirty flag honest when a value is changed and then changed back.
      baseline: "",
      cadenceRows: [],
      groupsLoaded: false,
      groupsError: "",
      // Draft state for the group gate, keyed by group id. The checkboxes
      // v-model against this rather than against the cadence rows, so a toggle
      // is free and instantly reflected, and nothing reaches the server until
      // Save. The previous one-way :checked binding could not restore itself
      // when a confirm was dismissed, leaving the box showing a state that was
      // never applied.
      gateDraft: {},
      gateBaseline: {},
      gateSaving: false,
      gateError: "",
    };
  },
  computed: {
    activePlan() {
      return this.plans.find((p) => p.interval_weeks === this.activeInterval) || null;
    },
    activeRows() {
      return this.activePlan ? this.activePlan.rows : [];
    },
    maxOffset() {
      return this.activeInterval * 7 - 1;
    },
    planIsInert() {
      return this.inertIntervals.includes(this.activeInterval);
    },
    // Flat rows regrouped for display: one block per message, its channels in
    // declared order. Rows are the same objects the plan holds, not copies, so
    // v-model on a channel's time or toggle still edits the plan directly.
    groupedRows() {
      const byTitle = new Map();
      for (const r of this.activeRows) {
        if (!byTitle.has(r.message_title)) byTitle.set(r.message_title, []);
        byTitle.get(r.message_title).push(r);
      }
      const order = (c) => CHANNELS.findIndex((x) => x.key === c);
      return MESSAGE_TYPES.filter((m) => byTitle.has(m.key)).map((m) => {
        const rows = byTitle
          .get(m.key)
          .slice()
          .sort((a, b) => order(a.channel) - order(b.channel));
        // Only worth surfacing where the body is generated per participant.
        // For the templated messages the two channels hit different providers
        // (Plivo and SMTP) and do not contend, so sharing a slot is harmless.
        const enabled = rows.filter((r) => r.enabled);
        const slotShared =
          enabled.length > 1 &&
          new Set(enabled.map((r) => r.send_time)).size < enabled.length;
        return {
          message_title: m.key,
          rows,
          contention: !!m.llmGenerated && slotShared,
        };
      });
    },
    dirty() {
      return this.loaded && JSON.stringify(this.plans) !== this.baseline;
    },
    // Which groups this plan actually governs, resolved through their current
    // cadence. Without this the page would be an abstract settings screen with
    // no indication of who it affects.
    // Full rows rather than names, since each chip is now a control that needs
    // the group's id and its own enabled state.
    // Groups running on this cadence right now.
    activeGroups() {
      return this.cadenceRows.filter(
        (r) => r.current && r.current.interval_weeks === this.activeInterval
      );
    },
    // Groups scheduled to move onto it but not there yet.
    //
    // Without these the 2-week tab reads "No group is on this cadence" for the
    // whole period between scheduling a change and its effective date, which is
    // true and looks like a bug. They belong in the gate as well as the count:
    // reminders_enabled is independent of cadence, so silencing a cohort ahead
    // of its launch is a legitimate thing to want to do.
    incomingGroups() {
      return this.cadenceRows.filter(
        (r) =>
          (!r.current || r.current.interval_weeks !== this.activeInterval) &&
          r.next_change &&
          r.next_change.interval_weeks === this.activeInterval
      );
    },
    affectedGroups() {
      return [...this.activeGroups, ...this.incomingGroups];
    },
    // Keyed lookup so a chip can show when a group actually arrives.
    incomingFrom() {
      const out = {};
      for (const g of this.incomingGroups) out[g.group_id] = g.next_change.effective_from;
      return out;
    },
    enabledGroupCount() {
      return this.affectedGroups.filter((g) => this.gateDraft[g.group_id]).length;
    },
    // Only the groups on this plan whose draft differs from what is saved. The
    // gate is scoped to the visible plan so Save can never quietly change a
    // group the admin is not looking at.
    gateChanges() {
      return this.affectedGroups.filter(
        (g) => this.gateDraft[g.group_id] !== this.gateBaseline[g.group_id]
      );
    },
    gateSilencing() {
      return this.gateChanges.filter((g) => !this.gateDraft[g.group_id]);
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(on) {
        if (on && !this.loaded) this.load();
      },
    },
  },
  methods: {
    meta(key) {
      return messageMeta(key) || { label: key, hint: "", logic: "", source: "" };
    },

    offsetHint(offset) {
      if (offset === 0) return "The first day of the period.";
      const week = Math.floor(offset / 7) + 1;
      return `${offset} day${offset === 1 ? "" : "s"} in (week ${week} of the period).`;
    },

    // Which zone a group's send times are read in. Mirrors the backend's
    // group_timezone() prefix rule (utils/helper.py) rather than being fetched,
    // because it is a label: getting it wrong shows a wrong abbreviation, it
    // does not change when anything sends. If the backend ever moves this onto a
    // groups column, this reads that column instead.
    groupZone(groupName) {
      return (groupName || "").toLowerCase().startsWith("military") ? "ET" : "CT";
    },

    channelLabel(key) {
      const c = CHANNELS.find((x) => x.key === key);
      return c ? c.label : key;
    },

    // The offset is edited once per message and written to every channel, so
    // the two rows cannot drift apart into "SMS on day 3, email on day 5".
    setOffset(group, event) {
      let value = parseInt(event.target.value, 10);
      if (!Number.isInteger(value)) value = 0;
      value = Math.min(Math.max(value, 0), this.maxOffset);
      for (const r of group.rows) r.day_offset = value;
    },

    async load() {
      this.loading = true;
      this.error = "";
      try {
        const data = await reminderApi.list();
        this.plans = data.plans || [];
        this.fixedRows = data.fixed || [];
        this.tickMinutes = data.tick_minutes ?? 30;
        this.inertIntervals = data.inert_intervals || [];
        if (this.plans.length) this.activeInterval = this.plans[0].interval_weeks;
        this.baseline = JSON.stringify(this.plans);
        this.loaded = true;
      } catch (e) {
        this.error = `Could not load the reminder schedule. ${e?.message || e}`;
      } finally {
        this.loading = false;
      }
      // Secondary and non-blocking: the schedule is editable whether or not we
      // can say which groups it currently covers. But the failure is reported
      // rather than swallowed, since an empty gate and an unreachable gate look
      // identical on screen otherwise.
      try {
        this.cadenceRows = await cadenceApi.listCadence();
        this.groupsError = "";
      } catch (e) {
        this.cadenceRows = [];
        this.groupsError =
          `Could not load which groups are on this cadence. ${e?.message || e}`;
      } finally {
        this.syncGate();
        this.groupsLoaded = true;
      }
    },

    revert() {
      this.plans = JSON.parse(this.baseline);
    },

    // Seed the gate draft from what the server reports. Called after every
    // successful read or write so draft and baseline never drift.
    syncGate() {
      const draft = {};
      const baseline = {};
      for (const g of this.cadenceRows) {
        draft[g.group_id] = !!g.reminders_enabled;
        baseline[g.group_id] = !!g.reminders_enabled;
      }
      this.gateDraft = draft;
      this.gateBaseline = baseline;
    },

    revertGate() {
      for (const g of this.gateChanges) {
        this.gateDraft[g.group_id] = this.gateBaseline[g.group_id];
      }
      this.gateError = "";
    },

    // Silencing is scoped to reminders: it does not gate sessions, and the
    // dispatcher skips a silenced group before claiming a slot, so re-enabling
    // mid-day leaves that day's schedule free to fire normally.
    //
    // One confirm for the whole batch, and only when something is being
    // silenced. Enabling is not destructive, and confirming every individual
    // toggle taught the admin to dismiss the dialog without reading it.
    async saveGate() {
      if (!this.gateChanges.length || this.gateSaving) return;

      const silencing = this.gateSilencing;
      if (silencing.length) {
        const names = silencing.map((g) => g.group_name).join(", ");
        const confirm = await swal.fire({
          icon: "warning",
          title:
            silencing.length === 1
              ? `Silence reminders for ${names}?`
              : `Silence reminders for ${silencing.length} groups?`,
          text:
            `${silencing.length === 1 ? "This group stops" : `${names} stop`} ` +
            "receiving all outbound SMS and email. Sessions and the cadence " +
            "itself are unaffected.",
          showCancelButton: true,
          confirmButtonText: "Silence",
          cancelButtonText: "Cancel",
        });
        // Dismissing leaves the draft exactly as it was, which is now safe:
        // the checkboxes reflect the draft, not the last click.
        if (!confirm.isConfirmed) return;
      }

      this.gateSaving = true;
      this.gateError = "";
      // Snapshot before the loop: gateChanges is computed from the baseline, so
      // it shrinks as each success updates that baseline.
      const pending = this.gateChanges.slice();
      const failed = [];
      // Sequential rather than parallel. Each is a separate PUT, and a partial
      // failure has to leave the baseline accurate per group rather than
      // wholesale, so the admin can see exactly which ones did not take and
      // retry only those.
      for (const g of pending) {
        const next = this.gateDraft[g.group_id];
        try {
          await cadenceApi.setRemindersEnabled(g.group_id, next);
          g.reminders_enabled = next;
          this.gateBaseline[g.group_id] = next;
        } catch (e) {
          failed.push(`${g.group_name} (${e?.message || e})`);
        }
      }
      this.gateSaving = false;

      if (failed.length) {
        this.gateError =
          `Could not update ${failed.length} of ${pending.length}: ` +
          `${failed.join("; ")}. ` +
          (failed.length === pending.length
            ? "Nothing was changed."
            : "The rest were saved; the failed ones are still shown as pending.");
      }
    },

    async save() {
      if (!this.dirty || !this.activePlan) return;

      const covered = this.affectedGroups.length;
      const confirm = await swal.fire({
        icon: "question",
        title: `Save the ${this.activeInterval}-week reminder plan?`,
        text: covered
          ? `This changes when ${covered} ${covered === 1 ? "group" : "groups"} ` +
            `(${this.affectedGroups.map((g) => g.group_name).join(", ")}) receive ` +
            "outbound messages. " +
            "Messages already sent are unaffected."
          : "No group is currently on this cadence, so nothing changes today.",
        showCancelButton: true,
        confirmButtonText: "Save",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      this.saving = true;
      try {
        await reminderApi.save(this.activeInterval, this.activePlan.rows);
        this.baseline = JSON.stringify(this.plans);
        swal.fire({ icon: "success", title: "Saved", text: "Reminder schedule updated." });
      } catch (e) {
        swal.fire({
          icon: "error",
          title: "Could not save",
          text: String(e?.message || e),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Matches ModelConfigPanel and SessionCadencePanel. */
.reminder-config {
  padding: 20px;
  background-color: #ffffffb2;
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 0 0 20px;
  max-width: none;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.hint {
  font-size: 0.9em;
  color: #444;
  margin-bottom: 16px;
}

.mock-note {
  font-size: 0.85em;
  color: #7a5b00;
  background: #fff8e6;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
}

.sub-hint {
  font-size: 0.86em;
  color: #666;
  margin: 0 0 14px;
}

.sub-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.94em;
}

.state {
  font-size: 0.85em;
  color: #666;
  margin: 6px 0;
}

.lint-error {
  color: #d33;
  font-size: 0.82em;
  margin: 6px 0 0;
}

.row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.plan-row {
  margin-bottom: 4px;
}

.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  flex: 1;
  min-width: 180px;
}

.field label {
  margin-bottom: 6px;
  font-weight: 600;
}

.radios {
  display: flex;
  gap: 20px;
}

.radio {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
  margin-bottom: 0;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 14px;
}

.sched-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #6c757d;
  border-radius: 4px;
}

/* Uppercased and filled a shade darker than the body, so the header reads as
   a heading rather than as the first row of the table. Matches .list-header
   on the cadence tab. */
.sched-table th {
  text-align: left;
  font-size: 0.84em;
  color: #4a5460;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px 12px;
  border-bottom: 1px solid #6c757d;
  background: #eceff2;
  white-space: nowrap;
}

/* Two levels of separation now that a message spans one row per channel: a
   hairline between an SMS row and its own email row, and the full divider
   only where one message ends and the next begins. Using one weight for both
   would make the channel pair look like two unrelated messages. */
.sched-table td {
  padding: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.09);
  vertical-align: top;
  font-size: 0.92em;
}

.sched-table tbody tr.group-start:not(:first-child) td {
  border-top: 1px solid #6c757d;
}

.sched-table tr:last-child td {
  border-bottom: none;
}

/* Advisory, not a validation error: sharing a slot is allowed and is only
   costly for the one message that generates its body per participant. */
.m-warn {
  font-size: 0.88em;
  line-height: 1.5;
  color: #7a5b00;
  background: #fff8e6;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  padding: 7px 10px;
  margin-top: 7px;
}

.channel-cell {
  font-weight: 600;
  color: #4a5460;
  white-space: nowrap;
}

.sched-table col.col-channel {
  width: 86px;
}

.sched-table .center {
  text-align: center;
}

.m-label {
  font-weight: 600;
}

/* Detail text sits at near-body size and takes the full column width. It was
   0.82em capped at 46ch, which wrapped the firing rules into a narrow ragged
   block right where they need to be read. */
.m-hint {
  font-size: 0.9em;
  color: #5c6570;
  margin-top: 4px;
  line-height: 1.5;
}

.m-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.92em;
}

/* The rule that actually decides whether a participant gets messaged. Set
   apart from the purpose line because "checked every day" and "messaged every
   day" are not the same claim, and the schedule column only says the former. */
/* Tinted with translucent black rather than a fixed light grey, so it darkens
   whatever surface it lands on instead of assuming one.
   The panel is #ffffffb2 over the theme background, which composites to about
   #f7f7f7 on the civil theme (#e4e4e4) -- a #f5f7f9 fill was within two points
   of that and vanished. An alpha tint steps down from the panel by the same
   amount on both themes and inherits their warmth or coolness for free. */
.m-logic {
  font-size: 0.9em;
  color: #3f4954;
  line-height: 1.55;
  margin-top: 7px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.1);
  border-left: 3px solid rgba(0, 0, 0, 0.32);
  border-radius: 0 4px 4px 0;
}

/* Takes the theme's own accent: navy #002F6C, civil #636967. Both carry
   enough weight against the tint to read as a label. */
.m-logic-tag {
  font-weight: 700;
  color: var(--primary-color);
  margin-right: 4px;
}

.m-source {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.88em;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.when-line {
  font-size: 0.95em;
  white-space: nowrap;
}

/* Read-only row: present for completeness, visibly not part of the plan. */
.fixed-row td {
  background: #fafbfc;
}

.ro-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 9px;
  background: #e9ecef;
  border: 1px solid #d6dbe0;
  color: #5c6570;
  font-size: 0.72em;
  font-weight: 700;
  vertical-align: middle;
  white-space: nowrap;
}

.ro-value {
  color: #6b7480;
  font-size: 0.95em;
}

/* Callout naming the groups this plan governs. */
/* Matches the schedule table's frame below it, so the two read as two sections
   of one page rather than a callout floating above a table. */
.gate-panel {
  border: 1px solid #6c757d;
  border-radius: 4px;
  overflow: hidden;
  margin: 4px 0 18px;
}

/* Translucent black rather than a fixed light grey: this panel renders over
   both the navy and civil surfaces, and a hardcoded #f5f7f9 composited to the
   same colour as the civil background, making the section invisible. */
.gate-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 14px;
  background: rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #6c757d;
  font-weight: 700;
  font-size: 0.86em;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.gate-count {
  font-weight: 600;
  font-size: 0.95em;
  letter-spacing: 0;
  text-transform: none;
  opacity: 0.75;
}

.gate-body {
  padding: 12px 14px 13px;
}

.gate-hint {
  margin: 0;
  font-size: 0.88em;
  line-height: 1.45;
  opacity: 0.8;
}

.gate-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 13px;
  padding-top: 11px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/* Pushes the two buttons to the right, keeping the pending count anchored to
   the chips it describes. */
.gate-pending {
  flex: 1;
  font-size: 0.85em;
  font-weight: 600;
  opacity: 0.75;
}

.applies-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 9px;
}

/* A control now, not a label: the checkbox is the switch and the chip is its
   hit area. */
.group-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 3px 11px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #b6d4fe;
  color: #1c4e80;
  font-size: 0.88em;
  font-weight: 600;
}

/* A silenced group stays listed -- it is still governed by this plan, it just
   receives nothing -- so it reads as struck through rather than disappearing. */
.group-chip.off {
  color: #7a8794;
  border-color: #d6dbe0;
  background: #f2f4f6;
  text-decoration: line-through;
}

/* An unsaved chip is marked so the pending count has something to point at.
   Without this, "2 changes pending" leaves the admin hunting for which two. */
.group-chip.changed {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}

/* Scheduled onto this cadence but not there yet. Dashed rather than faded so it
   stays legible: these are still real, still toggleable groups. */
.group-chip.incoming {
  border-style: dashed;
  opacity: 0.75;
}

.chip-from {
  font-weight: 500;
  font-size: 0.9em;
  opacity: 0.8;
}

/* The zone a group's send times are read in. Quiet, but present on every chip:
   a 09:00 row means 09:00 ET for military and 09:00 CT for civilian, and
   without this the table looks like one absolute time for everyone. */
.chip-tz {
  font-weight: 700;
  font-size: 0.78em;
  letter-spacing: 0.04em;
  opacity: 0.65;
}

.th-note {
  font-weight: 400;
  font-size: 0.82em;
  opacity: 0.7;
}

.group-chip input {
  cursor: pointer;
  margin: 0;
}

.applies-note {
  margin-top: 5px;
  font-size: 0.88em;
  color: #7a5b00;
}

/* Column widths: the message column takes whatever the controls do not. */
.sched-table col.col-when {
  width: 200px;
}

.sched-table col.col-time {
  width: 118px;
}

.sched-table col.col-on {
  width: 62px;
}

.sched-table input[type="number"],
.sched-table select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
}

.day-input {
  width: 64px;
}

.button-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.dirty-note {
  margin-right: auto;
  font-size: 0.85em;
  color: #7a5b00;
}

.btn {
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn.primary {
  background-color: var(--primary-color);
}

.btn.secondary {
  background-color: #6c757d;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
